// Deterministic seeded RNG (mulberry32) shared by run-map generation, mod
// drafts, and mission variants. Everything in a run derives from the run
// seed so a run can be reproduced, saved mid-run, and resumed identically.

export type Rng = () => number;

export function mulberry32(seed: number): Rng {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function randomSeed(): number {
  // Not part of the deterministic path — only mints NEW run seeds.
  return (Date.now() ^ (Math.random() * 0xffffffff)) >>> 0;
}

export function pickIndex(rng: Rng, length: number): number {
  return Math.min(length - 1, Math.floor(rng() * length));
}

// Weighted pick over items with a weight accessor; returns the index.
export function pickWeighted<T>(rng: Rng, items: readonly T[], weight: (item: T) => number): number {
  let total = 0;
  for (const item of items) total += Math.max(0, weight(item));
  if (total <= 0) return pickIndex(rng, items.length);
  let roll = rng() * total;
  for (let i = 0; i < items.length; i++) {
    roll -= Math.max(0, weight(items[i]!));
    if (roll <= 0) return i;
  }
  return items.length - 1;
}
